import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, Trash2, X } from 'lucide-react';
import { messagingAPI } from '../services/messaging';

const filters = [
  { value: 'all', label: 'All conversations' },
  { value: 'unread', label: 'Unread' },
  { value: 'archived', label: 'Archived' },
];

export default function ManageConversationsModal({ onClose, onUpdated }) {
  const [filter, setFilter] = useState('all');
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const selectedCount = selected.size;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (filter === 'all') {
        const [active, archived] = await Promise.all([
          messagingAPI.getConversations({ archived: false, limit: 200 }),
          messagingAPI.getConversations({ archived: true, limit: 200 }),
        ]);
        setList([...(active || []), ...(archived || [])]);
      } else if (filter === 'unread') {
        const unread = await messagingAPI.getConversations({ archived: false, filter: 'unread', limit: 200 });
        setList(unread || []);
      } else if (filter === 'archived') {
        const archived = await messagingAPI.getConversations({ archived: true, limit: 200 });
        setList(archived || []);
      }
      setSelected(new Set());
    } catch (e) {
      console.error('Manage conversations load failed:', e);
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = useCallback((conversationId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(conversationId)) {
        next.delete(conversationId);
      } else {
        next.add(conversationId);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      if (prev.size === list.length) return new Set();
      return new Set(list.map((c) => c.conversation_id));
    });
  }, [list]);

  const bulkArchive = useCallback(async () => {
    if (!selectedCount) return;
    const unarchive = filter === 'archived';
    setProcessing(true);
    try {
      await Promise.all(
        [...selected].map((cid) => messagingAPI.archiveConversation(cid, !unarchive))
      );
      await load();
      onUpdated?.();
    } catch (e) {
      console.error('Bulk archive failed:', e);
    } finally {
      setProcessing(false);
    }
  }, [selected, selectedCount, filter, load, onUpdated]);

  const bulkDelete = useCallback(async () => {
    if (!selectedCount) return;
    if (!window.confirm('Delete selected conversations? This cannot be undone for you.')) return;
    setProcessing(true);
    try {
      await Promise.all([...selected].map((cid) => messagingAPI.deleteConversation(cid)));
      await load();
      onUpdated?.();
    } catch (e) {
      console.error('Bulk delete failed:', e);
    } finally {
      setProcessing(false);
    }
  }, [selected, selectedCount, load, onUpdated]);

  const emptyText = useMemo(() => {
    if (filter === 'unread') return 'No unread conversations.';
    if (filter === 'archived') return 'No archived conversations.';
    return 'No conversations found.';
  }, [filter]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Manage conversations</h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <div className="p-4 border-b border-gray-100 flex items-center gap-3">
          <label className="text-sm text-gray-600">Filter</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 rounded-md text-sm px-3 py-1.5"
          >
            {filters.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          <div className="flex-1" />
          <button
            type="button"
            onClick={toggleAll}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            {selected.size === list.length && list.length > 0 ? 'Clear all' : 'Select all'}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-gray-500">Loading conversations...</div>
          ) : list.length === 0 ? (
            <div className="p-6 text-center text-gray-500">{emptyText}</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {list.map((c) => (
                <li key={c.conversation_id} className="p-4 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selected.has(c.conversation_id)}
                    onChange={() => toggle(c.conversation_id)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{c.display_name || 'Unknown'}</p>
                    <p className="text-sm text-gray-500 truncate">{c.last_message_preview || 'No messages yet'}</p>
                  </div>
                  {c.unread_count > 0 && (
                    <span className="text-xs px-2 py-1 rounded-full bg-primary-50 text-primary-700">
                      {c.unread_count} unread
                    </span>
                  )}
                  {c.archived && (
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                      Archived
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-600">{selectedCount} selected</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={bulkArchive}
              disabled={!selectedCount || processing}
              className="btn btn-outline flex items-center gap-2"
            >
              <Archive className="w-4 h-4" />
              {filter === 'archived' ? 'Unarchive selected' : 'Archive selected'}
            </button>
            <button
              type="button"
              onClick={bulkDelete}
              disabled={!selectedCount || processing}
              className="btn btn-outline text-red-600 border-red-200 hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
